import { useEffect, useRef, useState } from 'react';

/**
 * Hook to detect when an element enters the viewport
 * @param {Object} options - Intersection Observer options
 * @returns {Array} [ref, isIntersecting] - Ref to attach to element and intersection state
 */
export function useIntersectionObserver(options = {}) {
  const elementRef = useRef(null);
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsIntersecting(true);
      return;
    }
    const element = elementRef.current;
    if (!element) return;

    const defaultOptions = {
      threshold: 0.1,
      triggerOnce: true,
      ...options
    };

    const observer = new IntersectionObserver(([entry]) => {
      setIsIntersecting(entry.isIntersecting);
      
      // If triggerOnce is true, disconnect after first intersection
      if (entry.isIntersecting && defaultOptions.triggerOnce) {
        observer.disconnect();
      }
    }, {
      threshold: defaultOptions.threshold,
      rootMargin: defaultOptions.rootMargin || '0px'
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [options.threshold, options.rootMargin, options.triggerOnce]);

  return [elementRef, isIntersecting];
}
